import { useCallback } from "react";

import { AccountViewer } from "components/form/graphql/types";
import useTypedContext from "hooks/useTypedContext";
import { Action, Stack } from "types/generated";
import { AccessResult, hasAtLeastOneRequiredAction, NO_ACCESS_MESSAGE } from "utils/access";
import { AccountContext } from "views/AccountWrapper";

import { checkTemplateDeploymentAccess } from "./helpers/checkTemplateDeploymentAccess";
import useHasStackAdminAccess from "./useHasStackAdminAccess";

const STACK_UPDATE_ACCESS_TYPES = [Action.StackUpdate, Action.StackManage];

const hasStackUpdateAccess = (viewer: AccountViewer, spaceId?: string) =>
  hasAtLeastOneRequiredAction(STACK_UPDATE_ACCESS_TYPES, viewer, spaceId);

type StackForUpdate = Pick<Stack, "spaceDetails" | "space" | "isLockedByTemplateDeployment">;

/**
 * Hook that returns a function to check if the current user can update a stack, i.e. everything
 * backed by the `stackUpdate` mutation: the stack settings (details, source code, vendor, behavior)
 * and the run hooks.
 *
 * Moving a stack to another space goes through the same mutation but needs more than this - see
 * `useStackMoveSpaces`. Attaching contexts, policies or integrations is not part of it at all -
 * use `useCanManageStack` for those.
 *
 * Stacks created from a template deployment cannot be updated.
 * @returns {(stack?: StackForUpdate) => AccessResult} Function that takes a stack and returns AccessResult
 */
const useCanUpdateStack = (): ((stack?: StackForUpdate) => AccessResult) => {
  const hasAdminAccess = useHasStackAdminAccess();
  const { viewer } = useTypedContext(AccountContext);

  return useCallback(
    (stack) => {
      if (!stack) {
        return { result: false };
      }

      const templateDeploymentCheck = checkTemplateDeploymentAccess(stack);
      if (templateDeploymentCheck) {
        return templateDeploymentCheck;
      }

      const hasAccess =
        hasAdminAccess(stack).result ||
        hasStackUpdateAccess(viewer, stack.spaceDetails?.id || stack.space);

      if (hasAccess) {
        return { result: true };
      }

      return { result: false, message: NO_ACCESS_MESSAGE };
    },
    [hasAdminAccess, viewer]
  );
};

export default useCanUpdateStack;
